import React, { useEffect, useRef, useState } from "react";
import { Config } from "@eagerdog/config";

import { apiService } from "src/services/api.service";
import { httpService } from "src/services/http.service";
import { localStorageService } from "src/services/local-storage.service";
import { helperService } from "src/services/helper.service";
import { toast } from "src/components/Toast/ToastManager";

import PointsReminderModal from "./PointsReminderModal";
import { maxRedeemablePoints } from "./pointsReminder";

interface IAsk {
  redeemableAmount: number;
  onApply: (points: number) => Promise<number | undefined>;
  onContinue: () => void;
  onFree?: () => void;
}

export const usePointsReminder = (clubId: string | undefined, enabled: boolean) => {
  const [availablePoints, setAvailablePoints] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [maxPoints, setMaxPoints] = useState(0);
  const pending = useRef<IAsk | null>(null);

  useEffect(() => {
    if (!enabled || !clubId || !helperService.isLoggedIn()) {
      setAvailablePoints(0);
      return;
    }

    let cancelled = false;
    httpService.get(`${Config.web.REACT_APP_API_URL}/api/wallets/me/clubs/${clubId}/points`).then((response) => {
      if (!cancelled) setAvailablePoints(response.data?.points || 0);
    }).catch(() => {
      if (!cancelled) setAvailablePoints(0);
    });

    return () => {
      cancelled = true;
    };
  }, [clubId, enabled]);

  const shouldAsk = (redeemableAmount: number, pointsApplied: boolean) => {
    if (!enabled || dismissed || pointsApplied || open) return false;
    return maxRedeemablePoints(availablePoints, redeemableAmount) > 0;
  };

  const ask = (request: IAsk) => {
    pending.current = request;
    setMaxPoints(maxRedeemablePoints(availablePoints, request.redeemableAmount));
    setOpen(true);
  };

  const turnOff = async (dontRemind: boolean) => {
    if (!dontRemind) return;
    try {
      const updated = await apiService.updateSelf({ show_points_reminder: false });
      localStorageService.setItem(localStorageService.USER, JSON.stringify(updated));
    } catch (error) {
      toast.show({
        title: "Points reminder",
        content: "Couldn't save your preference. Checkout will continue.",
        duration: 8000,
        errorDetails: error as any,
        type: "fail",
      });
    }
  };

  const finish = async (dontRemind: boolean, points?: number) => {
    const request = pending.current;
    if (!request) {
      setOpen(false);
      setDismissed(true);
      return;
    }

    setBusy(true);
    try {
      await turnOff(dontRemind);
      let total: number | undefined;
      if (points) {
        total = await request.onApply(points);
        if (total === undefined) {
          return;
        }
      }
      setDismissed(true);
      setOpen(false);
      pending.current = null;
      if (points && total !== undefined && total <= 0 && request.onFree) {
        request.onFree();
      } else {
        request.onContinue();
      }
    } catch (error) {
      toast.show({
        title: "Points reminder",
        content: "Couldn't apply those points. Try the redeem box in the cart.",
        duration: 8000,
        errorDetails: error as any,
        type: "fail",
      });
    } finally {
      setBusy(false);
    }
  };

  const modal = React.createElement(PointsReminderModal, {
    isShown: open,
    busy,
    availablePoints,
    maxPoints,
    onRedeem: (points: number, dontRemind: boolean) => { void finish(dontRemind, points); },
    onContinue: (dontRemind: boolean) => { void finish(dontRemind); },
    onDismiss: () => {
      if (busy) return;
      setDismissed(true);
      setOpen(false);
      pending.current = null;
    },
  });

  return { shouldAsk, ask, modal, availablePoints };
};
